// Iter 82.7 / Phase E2.3 — Attendance HR Command Centre.
// Org-wide attendance KPIs, department rollup, top attendance risks,
// rolling N-day trend chart, top approval blockers. Powered by
// /api/attendance/dashboard/hr.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ChartLineUp, Warning, FileText, UsersThree, Hourglass,
    CheckCircle, XCircle, Sun, ShieldCheck,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const WINDOW_OPTIONS = [
    { v: 7, l: "Last 7 days" },
    { v: 14, l: "Last 14 days" },
    { v: 28, l: "Last 28 days" },
    { v: 90, l: "Last 90 days" },
];

const CLASS_TONE = {
    Excellent: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Good: "bg-sky-100 text-sky-800 border-sky-200",
    "Needs Attention": "bg-amber-100 text-amber-800 border-amber-200",
    Critical: "bg-rose-100 text-rose-800 border-rose-200",
};

function pctBand(pct) {
    if (pct >= 95) return "bg-emerald-500";
    if (pct >= 85) return "bg-amber-500";
    return "bg-rose-500";
}

function Kpi({ icon, label, value, suffix = "", tone = "slate", testid, to }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        orange: "bg-orange-50 border-orange-200 text-orange-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
    };
    const card = (
        <div className={`rounded-xl border p-4 ${map[tone]} ${to ? "hover:shadow transition cursor-pointer" : ""}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
                {icon}
            </div>
            <div className="text-2xl sm:text-3xl font-bold mt-2">{value}{suffix && <span className="text-sm font-normal opacity-70 ml-1">{suffix}</span>}</div>
        </div>
    );
    return to ? <Link to={to}>{card}</Link> : card;
}

export default function AttendanceHrDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/attendance/dashboard/hr", {
                params: { days_window: days },
            });
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load HR dashboard"));
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading || !data) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="att-hr-loading">
                <PageHeader eyebrow="Attendance" title="HR Attendance Dashboard" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    const k = data.kpis || {};
    const maxBar = Math.max(1, ...(data.trend_series || []).map((b) => b.working || 1));

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="att-hr-page">
            <PageHeader
                eyebrow="Attendance"
                title="HR Attendance Dashboard"
                description={`Org-wide command centre · scope: ${data.scope} · ${formatDate(data.today)}`}
                actions={(
                    <div className="flex gap-2 items-center">
                        <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                                className="text-xs border border-slate-300 rounded-md px-2 py-1.5"
                                data-testid="att-hr-window-select">
                            {WINDOW_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                        <Link to="/admin/attendance/reports/daily-summary">
                            <Button variant="outline" size="sm" data-testid="att-hr-daily-btn">Daily Report</Button>
                        </Link>
                        <Link to="/admin/attendance/reports/monthly-summary">
                            <Button size="sm" data-testid="att-hr-monthly-btn">Monthly Report</Button>
                        </Link>
                    </div>
                )}
            />

            {/* Top-row KPIs */}
            <section className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="att-hr-kpis">
                <Kpi testid="hr-kpi-att-pct" icon={<ChartLineUp size={18} />} label="Attendance % Today" value={k.attendance_pct_today?.toFixed(1) ?? "0.0"} suffix="%" tone="sky" />
                <Kpi testid="hr-kpi-present" icon={<CheckCircle size={18} />} label="Present Today" value={k.present_today || 0} tone="emerald"
                     to={`/admin/attendance/snapshots?from_date=${data.today}&to_date=${data.today}&attendance_status=Present`} />
                <Kpi testid="hr-kpi-absent" icon={<XCircle size={18} />} label="Absent Today" value={k.absent_today || 0} tone="rose"
                     to={`/admin/attendance/snapshots?from_date=${data.today}&to_date=${data.today}&attendance_status=Absent`} />
                <Kpi testid="hr-kpi-late" icon={<Hourglass size={18} />} label="Late Today" value={k.late_today || 0} tone="amber" />
                <Kpi testid="hr-kpi-onleave" icon={<Sun size={18} />} label="On Leave Today" value={k.on_leave_today || 0} tone="violet" />
                <Kpi testid="hr-kpi-halfday" icon={<Hourglass size={18} />} label="Half Day Today" value={k.half_day_today || 0} tone="amber" />
            </section>

            {/* Second-row KPIs — Operational queue */}
            <section className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="att-hr-ops">
                <Kpi testid="hr-kpi-open-anomalies" icon={<Warning size={18} />} label="Open Anomalies" value={k.open_anomalies || 0} tone="amber" to="/attendance/anomalies?scope=all" />
                <Kpi testid="hr-kpi-critical-anomalies" icon={<Warning size={18} />} label="Critical Anomalies" value={k.critical_anomalies || 0} tone="rose" to="/attendance/anomalies?scope=all&severity=Critical" />
                <Kpi testid="hr-kpi-pending-regs" icon={<FileText size={18} />} label="Pending Regularizations" value={k.pending_regularizations || 0} tone="amber" to="/regularization/requests?scope=all&status_filter=Pending" />
                <Kpi testid="hr-kpi-overdue-3d" icon={<Hourglass size={18} />} label="Reg Overdue > 3d" value={k.regularizations_overdue_3d || 0} tone="orange" to="/regularization/requests?scope=all&status_filter=Pending" />
                <Kpi testid="hr-kpi-overdue-7d" icon={<Warning size={18} />} label="Reg Overdue > 7d" value={k.regularizations_overdue_7d || 0} tone="rose" to="/regularization/requests?scope=all&status_filter=Pending" />
            </section>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Trend chart */}
                <section className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5" data-testid="att-hr-trend">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <ChartLineUp size={14} /> Attendance Trend · {data.days_window} day window
                        </h3>
                    </div>
                    <div className="flex items-end gap-2 h-44" data-testid="att-hr-trend-bars">
                        {data.trend_series.map((b) => (
                            <div key={b.date} className="flex-1 flex flex-col items-center gap-1 group" data-testid={`trend-bar-${b.date}`}>
                                <div className="text-[9px] text-slate-500 font-mono opacity-0 group-hover:opacity-100 transition">{b.attendance_pct.toFixed(0)}%</div>
                                <div className="w-full bg-slate-100 rounded-t flex items-end" style={{ height: "100%" }}>
                                    <div className={`w-full rounded-t ${pctBand(b.attendance_pct)} transition-all`}
                                         style={{ height: `${(b.attendance_pct / 100) * 100}%` }}
                                         title={`${b.date}: ${b.attendance_pct.toFixed(1)}% · ${b.working} working · ${b.late} late`} />
                                </div>
                                <div className="text-[9px] text-slate-600 font-mono">{b.date.slice(5)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 flex gap-4 text-[10px] text-slate-500 justify-center">
                        <span className="flex items-center gap-1"><div className="w-2 h-2 bg-emerald-500 rounded-sm" />≥95%</span>
                        <span className="flex items-center gap-1"><div className="w-2 h-2 bg-amber-500 rounded-sm" />85-95%</span>
                        <span className="flex items-center gap-1"><div className="w-2 h-2 bg-rose-500 rounded-sm" />&lt;85%</span>
                    </div>
                </section>

                {/* Top blockers */}
                <section className="bg-white rounded-xl border border-slate-200" data-testid="att-hr-blockers">
                    <div className="px-5 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <UsersThree size={14} /> Top Approval Blockers
                        </h3>
                        <p className="text-[10px] text-slate-500 mt-0.5">Approvers sitting on the most pending regularizations</p>
                    </div>
                    {data.top_blockers.length === 0 ? (
                        <div className="text-center py-8 text-sm text-slate-400">No pending regularizations.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {data.top_blockers.map((b, idx) => (
                                <li key={b.key} className="px-5 py-2.5 flex items-center justify-between" data-testid={`blocker-${idx}`}>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-800">{b.full_name || b.key}</div>
                                        <div className="text-xs text-slate-500">{b.employee_code || "—"}</div>
                                    </div>
                                    <Badge className="bg-rose-100 text-rose-800 text-xs">{b.count} pending</Badge>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            {/* Top Attendance Risks — operator-specified columns */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="att-hr-top-risks">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <ShieldCheck size={14} /> Top Attendance Risks · last 30 days
                    </h3>
                    <Link to="/admin/attendance/reports/monthly-summary" className="text-xs text-slate-500 hover:underline" data-testid="att-hr-risks-link">View monthly report</Link>
                </div>
                {data.top_attendance_risks.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400">No risk profiles in the last 30 days. Healthy org.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-right px-3 py-2 font-medium">Risk Score</th>
                                <th className="text-left px-3 py-2 font-medium">Classification</th>
                                <th className="text-left px-3 py-2 font-medium">Primary Risk Factor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_attendance_risks.map((r) => (
                                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                    onClick={() => window.location.assign(`/admin/attendance/snapshots?employee_id=${r.employee_id}`)}
                                    data-testid={`risk-row-${r.employee_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="text-sm font-semibold text-slate-800">{r.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-xs">{r.department || "—"}</td>
                                    <td className="px-3 py-2 text-right font-bold text-slate-900" data-testid={`risk-score-${r.employee_id}`}>{r.reliability_score.toFixed(1)}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-xs ${CLASS_TONE[r.reliability_classification] || "bg-slate-100"}`}>{r.reliability_classification}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-700">{r.primary_risk_factor}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Department rollup */}
            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="att-hr-dept-rollup">
                <div className="px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800">Department Rollup · Today</h3>
                </div>
                {data.department_rollup.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400">No department-tagged snapshots today.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Department</th>
                                <th className="text-right px-3 py-2 font-medium">Total</th>
                                <th className="text-right px-3 py-2 font-medium">Present</th>
                                <th className="text-right px-3 py-2 font-medium">Absent</th>
                                <th className="text-right px-3 py-2 font-medium">Half Day</th>
                                <th className="text-right px-3 py-2 font-medium">On Leave</th>
                                <th className="text-right px-3 py-2 font-medium">Late</th>
                                <th className="text-right px-3 py-2 font-medium">Attendance %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.department_rollup.map((d) => (
                                <tr key={d.department} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`dept-row-${d.department}`}>
                                    <td className="px-3 py-2 font-semibold text-slate-800">{d.department}</td>
                                    <td className="px-3 py-2 text-right">{d.total}</td>
                                    <td className="px-3 py-2 text-right text-emerald-700">{d.Present}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{d.Absent}</td>
                                    <td className="px-3 py-2 text-right text-amber-700">{d["Half Day"]}</td>
                                    <td className="px-3 py-2 text-right text-sky-700">{d["On Leave"]}</td>
                                    <td className="px-3 py-2 text-right text-orange-700">{d.late}</td>
                                    <td className="px-3 py-2 text-right">
                                        <span className={`font-bold ${d.attendance_pct >= 95 ? "text-emerald-700" : d.attendance_pct >= 85 ? "text-amber-700" : "text-rose-700"}`}>
                                            {d.attendance_pct.toFixed(1)}%
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
